import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../api/client';
import './Account.css';
// reuse reviews hero styles for a matching header look
import '../Reviews/Reviews.css';

// month names will be loaded from translations via LanguageContext
let MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const TIER_ICONS = { Wanderer:'�', Explorer:'🧭', 'Forest Keeper':'🌲', 'Mountain Guardian':'⛰️', 'Legend of the Peaks':'👑' };
const TIER_COLORS = { Wanderer:'#7a9478', Explorer:'#0C6B37', 'Forest Keeper':'#094A25', 'Mountain Guardian':'#c9853a', 'Legend of the Peaks':'#F8B324' };

function fmtDate(d) { const x = new Date(d); return MONTHS[x.getMonth()] + ' ' + x.getDate() + ', ' + x.getFullYear(); }
function nights(a, b) { return Math.round((new Date(b) - new Date(a)) / 864e5); }
function bkStatus(b) {
  const t = new Date(); t.setHours(0,0,0,0);
  if (b.status === 'cancelled') return 'cancelled';
  if (t < new Date(b.check_in)) return 'upcoming';
  if (t < new Date(b.check_out)) return 'active';
  return 'completed';
}

export default function AccountPage() {
  const { user, signOut, updateProfile } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const locale = language === 'ro' ? 'ro-RO' : 'en-US';

  // load translated months if available
  try { MONTHS = t('account.months'); } catch (e) { /* fallback above */ }
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => { if (user) { setFullName(user.full_name || ''); setPhone(user.phone || ''); } }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const [br, sr] = await Promise.all([api.getMyBookings(), api.getMyStats()]); setBookings(br.bookings || []); setStats(sr); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setProfileMsg({ type: '', text: '' });
    const { error } = await updateProfile({ full_name: fullName, phone });
  if (error) setProfileMsg({ type: 'error', text: error.message });
  else { setProfileMsg({ type: 'success', text: t('account.profileSuccess') }); setTimeout(() => setProfileMsg({ type: '', text: '' }), 3000); }
    setSaving(false);
  };

  const handleCancel = async (id) => {
  if (!window.confirm(t('account.bookingCancelPrompt'))) return;
    setCancellingId(id);
    try { await api.cancelBooking(id); fetchData(); } catch (e) { alert(e.message); }
    setCancellingId(null);
  };

  const doSignOut = async () => { await signOut(); navigate('/login'); };

  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

  const filtered = bookings.filter(b => {
    const s = bkStatus(b);
    if (tab === 'upcoming') return s === 'upcoming' || s === 'active';
    if (tab === 'past') return s === 'completed';
    return s === 'cancelled';
  });

  const loyalty = stats?.loyalty;

  return (
    <div className="acc page-enter">
      {/* ── Hero Banner (styled like Reviews) ── */}
  <section className="rv rv__hero acc__hero">
        <div className="rv__hero-bg">
          <div className="rv__hero-gradient" />
          <div className="rv__hero-pattern" />
          <div className="rv__hero-vignette" />
        </div>

        {/* Floating particles */}
        <div className="rv__hero-particles">
          {[...Array(20)].map((_, i) => (
            <span key={i} className="rv__hero-particle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 6}s`,
              animationDuration: `${5 + Math.random() * 7}s`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              opacity: 0.12 + Math.random() * 0.22,
            }} />
          ))}
        </div>

        {/* Decorative leaves */}
        <div className="rv__hero-leaf rv__hero-leaf--1" />
        <div className="rv__hero-leaf rv__hero-leaf--2" />
        <div className="rv__hero-leaf rv__hero-leaf--3" />

        {/* Simple mountain silhouette for continuity */}
        <div className="rv__hero-mountains">
          <svg className="rv__hero-mountain rv__hero-mountain--back" viewBox="0 0 1440 200" preserveAspectRatio="none">
            <path d="M0,200 L0,120 Q180,40 360,100 Q540,30 720,80 Q900,20 1080,70 Q1260,40 1440,90 L1440,200 Z" />
          </svg>
          <svg className="rv__hero-mountain rv__hero-mountain--front" viewBox="0 0 1440 200" preserveAspectRatio="none">
            <path d="M0,200 L0,150 Q200,100 400,140 Q600,80 800,130 Q1000,90 1200,120 Q1350,105 1440,140 L1440,200 Z" />
          </svg>
        </div>

        <div className="rv__hero-content" data-reveal>
          <div className="rv__hero-tagline">
            <span className="rv__hero-tagline-line" />
            <span className="rv__hero-tagline-text">{t('account.heroTag')}</span>
            <span className="rv__hero-tagline-line" />
          </div>
          <h1 className="rv__hero-title">{t('account.welcomeBack')}, <em>{user?.full_name || t('account.traveler')}</em></h1>
          <p className="rv__hero-sub">{t('account.heroDesc')}</p>

          <div className="rv__hero-quill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.24 12.24a6 6 0 00-8.49-8.49L5 10.5V19h8.5z"/>
              <line x1="16" y1="8" x2="2" y2="22"/>
            </svg>
            <div className="rv__hero-quill-trail" />
          </div>
        </div>

        {/* Book spine decoration */}
        <div className="rv__hero-book-spine">
          <div className="rv__hero-spine-line" />
          <div className="rv__hero-spine-diamond" />
          <div className="rv__hero-spine-line" />
        </div>
      </section>

      {/* ── Stats Ribbon ── */}
      {stats && (
        <section className="acc__stats-section" data-reveal>
          <div className="acc__stats">
            <div className="acc__stat">
              <span className="acc__stat-icon">🏡</span>
              <span className="acc__stat-num">{stats.totalBookings}</span>
              <span className="acc__stat-label">{t('account.statsTotalStays')}</span>
            </div>
            <div className="acc__stat-sep" />
            <div className="acc__stat">
              <span className="acc__stat-icon">🌙</span>
              <span className="acc__stat-num">{stats.totalNights}</span>
              <span className="acc__stat-label">{t('account.statsNights')}</span>
            </div>
            <div className="acc__stat-sep" />
            <div className="acc__stat">
              <span className="acc__stat-icon">📅</span>
              <span className="acc__stat-num">{stats.upcoming}</span>
              <span className="acc__stat-label">{t('account.statsUpcoming')}</span>
            </div>
            <div className="acc__stat-sep" />
            <div className="acc__stat">
              <span className="acc__stat-icon">👥</span>
              <span className="acc__stat-num">{stats.totalGuests}</span>
              <span className="acc__stat-label">{t('account.statsGuests')}</span>
            </div>
            {stats.favouriteMonth && (
              <>
                <div className="acc__stat-sep" />
                <div className="acc__stat">
                  <span className="acc__stat-icon">⭐</span>
                  <span className="acc__stat-num acc__stat-num--text">{stats.favouriteMonth}</span>
                  <span className="acc__stat-label">Favourite Month</span>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ── Main Grid ── */}
      <section className="acc__main">
        <div className="acc__grid">
          {/* LEFT COLUMN */}
          <div className="acc__left">
            {/* Loyalty Card */}
            {loyalty && (
              <div className="acc__card acc__loyalty" data-reveal="left">
                <div className="acc__card-head">
                  <span className="acc__card-tag">{t('account.loyaltyTag')}</span>
                </div>
                <div className="acc__loyalty-tier">
                  <div className="acc__loyalty-sigil" style={{ borderColor: TIER_COLORS[loyalty.tier], background: TIER_COLORS[loyalty.tier] + '12' }}>
                    <span className="acc__loyalty-icon">{TIER_ICONS[loyalty.tier]}</span>
                  </div>
                  <div className="acc__loyalty-info">
                    <h3 style={{ color: TIER_COLORS[loyalty.tier] }}>{loyalty.tier}</h3>
                    {loyalty.discount > 0 && (
                      <div className="acc__loyalty-badge" style={{ borderColor: TIER_COLORS[loyalty.tier] + '44', color: TIER_COLORS[loyalty.tier], background: TIER_COLORS[loyalty.tier] + '0a' }}>
                        {loyalty.discount}% {t('account.loyaltyDiscount')}
                      </div>
                    )}
                  </div>
                </div>
                {loyalty.nextTier && (
                  <div className="acc__loyalty-progress">
                    <div className="acc__loyalty-bar">
                      <div className="acc__loyalty-bar-fill" style={{ width: loyalty.progress + '%', background: 'linear-gradient(90deg, ' + TIER_COLORS[loyalty.tier] + '88, ' + TIER_COLORS[loyalty.tier] + ')' }} />
                    </div>
                    <p className="acc__loyalty-next">
                      {loyalty.bookingsToNext} more stay{loyalty.bookingsToNext !== 1 ? 's' : ''} to <strong style={{ color: TIER_COLORS[loyalty.nextTier] }}>{loyalty.nextTier}</strong>
                    </p>
                  </div>
                )}
                <div className="acc__loyalty-roadmap">
      {Object.entries(TIER_ICONS).map(([tier, icon], idx) => {
                    const th = [0, 3, 5, 10, 20];
                    const reached = stats.totalBookings >= th[idx];
                    return (
                      <div key={tier} className={'acc__loyalty-step' + (reached ? ' acc__loyalty-step--done' : '') + (tier === loyalty.tier ? ' acc__loyalty-step--active' : '')}>
                        <span className="acc__loyalty-step-icon">{icon}</span>
                        <span className="acc__loyalty-step-name">{tier}</span>
        <span className="acc__loyalty-step-req">{th[idx]}+ {t('account.loyaltyStays')}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Profile Card */}
            <div className="acc__card acc__profile" data-reveal="left">
              <div className="acc__card-head">
                <span className="acc__card-tag">{t('account.profileTag')}</span>
                <button className="acc__signout" onClick={doSignOut}>{t('account.profileSignOut')}</button>
              </div>
              <div className="acc__profile-info">
                <div className="acc__profile-row">
                  <span className="acc__profile-row-icon">✉️</span>
                  <div>
                    <span className="acc__profile-row-label">{t('account.profileEmail')}</span>
                    <span className="acc__profile-row-val">{user?.email || '—'}</span>
                  </div>
                </div>
                <div className="acc__profile-row">
                  <span className="acc__profile-row-icon">📆</span>
                  <div>
                    <span className="acc__profile-row-label">{t('account.profileJoined')}</span>
                    <span className="acc__profile-row-val">{memberSince}</span>
                  </div>
                </div>
              </div>
              <form onSubmit={handleSave} className="acc__profile-form">
                {profileMsg.text && <div className={'acc__msg acc__msg--' + profileMsg.type}>{profileMsg.text}</div>}
                <div className="acc__profile-fields">
                  <div className="acc__field">
                    <label>{t('account.profileName')}</label>
                    <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder={t('account.profileName')} />
                  </div>
                  <div className="acc__field">
                    <label>{t('account.profilePhone')}</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('account.profilePhone')} />
                  </div>
                </div>
                <button className="acc__btn acc__btn--save" type="submit" disabled={saving}>
                  {saving ? t('account.profileSaving') : t('account.profileSave')}
                </button>
              </form>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="acc__right" data-reveal="right">
            <div className="acc__card acc__bookings">
              <div className="acc__card-head">
                <span className="acc__card-tag">{t('account.heroTag')}</span>
                <Link to="/book" className="acc__btn acc__btn--new">+ {t('footer.navigate.book')}</Link>
              </div>
              <div className="acc__tabs">
                {[
                  { key: 'upcoming', label: t('account.tabsUpcoming'), count: bookings.filter(b => { const s = bkStatus(b); return s === 'upcoming' || s === 'active'; }).length },
                  { key: 'past', label: t('account.tabsPast'), count: bookings.filter(b => bkStatus(b) === 'completed').length },
                  { key: 'cancelled', label: t('account.tabsCancelled'), count: bookings.filter(b => bkStatus(b) === 'cancelled').length },
                ].map(t => (
                  <button key={t.key} className={'acc__tab' + (tab === t.key ? ' acc__tab--active' : '')} onClick={() => setTab(t.key)}>
                    {t.label}
                    {t.count > 0 && <span className="acc__tab-count">{t.count}</span>}
                  </button>
                ))}
              </div>
              <div className="acc__booking-list">
                {loading ? (
                  <div className="acc__empty">
                    <div className="acc__spinner" />
                    <p>{t('account.profileSaving') /* fallback to a short loading label if no dedicated key */}</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="acc__empty">
                    <span className="acc__empty-icon">{tab === 'upcoming' ? '🌄' : tab === 'past' ? '📖' : '💨'}</span>
                    <p>{t('account.bookingNoStays')}</p>
                    {tab === 'upcoming' && <Link to="/book" className="acc__btn acc__btn--outline">{t('footer.navigate.book')}</Link>}
                  </div>
                ) : filtered.map(bk => {
                  const st = bkStatus(bk);
                  const n = nights(bk.check_in, bk.check_out);
                  return (
                    <div key={bk.id} className={'acc__booking acc__booking--' + st}>
                      <div className="acc__booking-badge">
                        <span className="acc__booking-badge-day">{new Date(bk.check_in).getDate()}</span>
                        <span className="acc__booking-badge-month">{MONTHS[new Date(bk.check_in).getMonth()]}</span>
                      </div>
                      <div className="acc__booking-body">
                        <div className="acc__booking-range">{fmtDate(bk.check_in)} → {fmtDate(bk.check_out)}</div>
                        <div className="acc__booking-meta">
                          <span>🌙 {n} {t('book.nights')}</span>
                          <span>👥 {bk.guests}</span>
                          {bk.notes && <span title={bk.notes}>📝</span>}
                        </div>
                      </div>
                      <div className="acc__booking-end">
                        <span className={'acc__booking-status acc__booking-status--' + st}>
                          {st === 'active' ? '● ' + t('account.statusActive') : st === 'upcoming' ? t('account.statusUpcoming') : st === 'completed' ? '✓ ' + t('account.statusCompleted') : '✕ ' + t('account.statusCancelled')}
                        </span>
                        {st === 'upcoming' && (
                          <button className="acc__booking-cancel" onClick={() => handleCancel(bk.id)} disabled={cancellingId === bk.id}>
                            {cancellingId === bk.id ? '...' : t('account.bookingCancelBtn')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}